// "Allow the assistant to read this": capability grants to zehn-aip
// (docs-api ZEHN_GRANTS=on, README "Capability grants").
//
// The person, here and now, lets zehn-aip read a folder, a document or a set
// of them with nobody present. Everything happens at docs, from the person's
// own docs session (the BFF cookie, through the app's axios client):
//
//   issue   POST   /embed/v1/grants {grantee: zehn-aip, objects: ["folder:<uuid>",
//                  "file:<uuid>"], actions: ["read"], ttl_days}   shares:write + files:read,
//                  and the person may share every object (docs-api decides)
//   list    GET    /embed/v1/grants?object=&grantee=zehn-aip       files:read: the issuer's own,
//                  and those naming an object the person may share
//   revoke  DELETE /embed/v1/grants/{id}                           the issuer, or whoever may share
//                  an object it names and holds shares:write
//
// aip collects its live grants itself (`GET /embed/v1/grants/issued-to-me`,
// an ASK as itself) and reads with them; nothing goes from this page to aip,
// and no aip credential is in the browser. The issue answer carries the
// grant's capability token once: it is dropped here at once, never kept or
// shown. A revoked grant refuses aip at its next read.
//
// Offered only where docs-api has grants on (it answers 404 otherwise:
// `grantsAvailable` asks once per page), to someone holding `shares:write`
// on nodes whose `access.can` says "share".

import { useEffect, useState } from 'react';
import baseAxios from '@/shared/utils/request';
import { iHold } from '@/shared/auth/me.js';
import { SCOPE } from '@/shared/auth/scopes.js';
import { GRANT, GRANTS } from '@/shared/utils/urls';
import { can } from '@/shared/utils/nodeAccess.js';

export const AIP_MODULE = 'zehn-aip';
// The only grantee docs issues to, and the only action.
export const GRANTEE = AIP_MODULE;
// How long a grant lasts (days; docs allows 1-90).
export const TTL_CHOICES = [7, 30, 90];
export const DEFAULT_TTL = 30;
// Listing several objects at once asks docs for every grant to zehn-aip the
// person may see, then keeps those naming one of them.
const LIST_LIMIT = 500;

let available = null; // promise of whether docs-api has grants on, once per page

/**
 * Whether docs-api issues grants here (ZEHN_GRANTS=on): a one-row list
 * answers 200; off, it answers 404. Any refusal reads as "not here".
 */
export function grantsAvailable() {
  if (!available) {
    available = baseAxios({ method: 'GET', url: GRANTS, params: { grantee: GRANTEE, limit: 1 } })
      .then(() => true)
      .catch(() => false);
  }
  return available;
}

/** `grantsAvailable` as React state: false until docs-api said yes; not asked without `shares:write`. */
export function useGrantsAvailable() {
  const [on, setOn] = useState(false);
  const allowed = iHold(SCOPE.SHARES_WRITE);
  useEffect(() => {
    if (!allowed) return undefined;
    let live = true;
    grantsAvailable().then((value) => {
      if (live) setOn(value);
    });
    return () => {
      live = false;
    };
  }, [allowed]);
  return allowed && on;
}

/** Test seam: ask docs-api again. */
export const resetGrantsAvailable = () => {
  available = null;
};

const isFolder = (node) => (node?.type || node?.nodeType || node?.node_type) === 'folder';

/** docs' name of a node in a grant: `folder:<uuid>` or `file:<uuid>`. */
export const objectName = (node) => `${isFolder(node) ? 'folder' : 'file'}:${node.uuid}`;

/** Whether "Allow the assistant" may be offered on these nodes: `shares:write`, and "share" on each. */
export const mayAllow = (nodes) =>
  iHold(SCOPE.SHARES_WRITE) && Array.isArray(nodes) && nodes.length > 0 && nodes.every((node) => node?.uuid && can(node, 'share'));

/** Whether a grant (as docs lists it) names one of `names`. */
export const namesAny = (grant, names) => (grant?.objects || []).some((name) => names.includes(name));

/**
 * The live grants to zehn-aip on these nodes that the person may see: for
 * one node, docs filters by its name; for several, every grant to zehn-aip
 * the person sees, narrowed here.
 */
export async function listGrants(nodes) {
  const names = nodes.map(objectName);
  const params = names.length === 1 ? { object: names[0], grantee: GRANTEE } : { grantee: GRANTEE, limit: LIST_LIMIT };
  const { data } = await baseAxios({ method: 'GET', url: GRANTS, params });
  const items = Array.isArray(data?.items) ? data.items : [];
  return items.filter((grant) => grant.active !== false && namesAny(grant, names));
}

/**
 * Issue one grant naming every node, for `ttlDays`. Answers the grant only:
 * the token in docs' answer is aip's to collect, so it is not handed on.
 */
export async function issueGrant(nodes, ttlDays = DEFAULT_TTL) {
  const { data } = await baseAxios({
    method: 'POST',
    url: GRANTS,
    data: { grantee: GRANTEE, objects: nodes.map(objectName), actions: ['read'], ttlDays },
  });
  return data?.grant ?? null;
}

/** Revoke a grant at docs (aip is refused at its next read). */
export async function revokeGrant(id) {
  const { data } = await baseAxios({ method: 'DELETE', url: GRANT.replace('{id}', encodeURIComponent(id)) });
  return data;
}
